import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getTenantProvisioningOverview, getAllTenantProvisioningRuns } from '../api/tenantProvisioningApi';
import { colorTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';
import { formatProvisioningMode } from '../utils/systemLabels';

const { semantic } = colorTokens;

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function modeTone(mode) {
  if (!mode) return 'Neutral';
  if (mode === 'dry_run' || String(mode).includes('dry')) return 'Info';
  if (String(mode).includes('record')) return 'Neutral';
  if (String(mode).includes('create')) return 'Info';
  return 'Neutral';
}

export function TenantProvisioningDashboardPage() {
  const { t } = useTranslation(['tenantProvisioning', 'common', 'statuses']);
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewResponse, runsResponse] = await Promise.all([
        getTenantProvisioningOverview(),
        getAllTenantProvisioningRuns({ per_page: 20 }),
      ]);
      setOverview(overviewResponse.data || overviewResponse);
      setRuns(runsResponse.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const data = overview || {};
  const recordCount = runs.length;

  const metricCards = useMemo(
    () => [
      { key: 'total', label: t('tenantProvisioning:dashboard.metrics.total'), value: data.total_runs ?? 0 },
      { key: 'completed', label: t('tenantProvisioning:dashboard.metrics.completed'), value: data.completed_count ?? 0 },
      { key: 'failed', label: t('tenantProvisioning:dashboard.metrics.failed'), value: data.failed_count ?? 0 },
      { key: 'dry_runs', label: t('tenantProvisioning:dashboard.metrics.dry_runs'), value: data.dry_run_count ?? 0 },
      {
        key: 'mode',
        label: t('tenantProvisioning:dashboard.metrics.mode'),
        value: formatProvisioningMode(data.mode) || data.mode || emptyValue,
      },
    ],
    [data.completed_count, data.dry_run_count, data.failed_count, data.mode, data.total_runs, emptyValue, t],
  );

  const columns = useMemo(
    () => [
      {
        id: 'tenant_id',
        label: t('tenantProvisioning:dashboard.columns.tenant'),
        render: (row) => (
          row.tenant_name
          || t('tenantProvisioning:dashboard.tenant_link', { id: row.tenant_id })
          || emptyValue
        ),
      },
      {
        id: 'run_type',
        label: t('tenantProvisioning:dashboard.columns.run_type'),
        render: (row) => row.run_type || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="provisioning_run" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'mode',
        label: t('tenantProvisioning:dashboard.columns.mode'),
        render: (row) => (
          row.mode
            ? (
              <StatusBadge
                status={row.mode}
                category="generic"
                apiLabel={formatProvisioningMode(row.mode) || row.mode}
                size="Sm"
                tone={modeTone(row.mode)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'database_name',
        label: t('tenantProvisioning:dashboard.columns.database'),
        render: (row) => (
          row.database_name
            ? <Typography sx={monoSx}>{row.database_name}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'updated_at',
        label: t('tenantProvisioning:dashboard.columns.updated'),
        render: (row) => {
          if (!row.updated_at) return emptyValue;
          const date = new Date(row.updated_at);
          return Number.isNaN(date.getTime()) ? row.updated_at : date.toLocaleString();
        },
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              if (row.tenant_id) {
                navigate(`/tenants/${row.tenant_id}`);
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('tenantProvisioning:dashboard.title')}
        subtitle={t('tenantProvisioning:dashboard.subtitle')}
      />

      {!loading && !data.database_creation_enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('tenantProvisioning:warnings.database_creation_disabled')}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('tenantProvisioning:dashboard.load_failed')}
        </Alert>
      )}

      {loading ? (
        <>
          <SectionCard variant="Default" sx={{ mb: 2.5 }}>
            <LoadingSkeleton type="CardGrid" rows={5} />
          </SectionCard>
          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('tenantProvisioning:dashboard.recent_runs')}
            />
            <LoadingSkeleton type="Table" embedded rows={3} columns={7} />
          </SectionCard>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
              mb: 2.5,
            }}
          >
            {metricCards.map((card) => (
              <MetricCard
                key={card.key}
                value={card.value}
                label={card.label}
              />
            ))}
          </Box>

          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('tenantProvisioning:dashboard.recent_runs')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('tenantProvisioning:dashboard.toolbar_count', { count: recordCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />

            <DataTable
              type="Default"
              embedded
              columns={columns}
              rows={runs}
              getRowKey={(row) => row.id}
              emptyMessage={t('tenantProvisioning:dashboard.no_runs')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': {
                  width: '100%',
                  minWidth: '100%',
                },
              }}
            />
          </SectionCard>
        </>
      )}
    </Box>
  );
}
